import React, { useState, useEffect } from 'react';
import { TrendingDown, AlertTriangle, Users, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

export const PremiumSilentEpidemicSection = () => {
  const { t } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const stats = [
    {
      icon: TrendingDown,
      value: '70%',
      description: t('silentEpidemicNew.stat1'),
      gradient: 'from-red-500 via-red-600 to-pink-600',
      bgGradient: 'from-red-50 to-pink-50'
    },
    {
      icon: AlertTriangle,
      value: `€2.3 ${t('silentEpidemicNew.billion')}`,
      description: t('silentEpidemicNew.stat2'),
      gradient: 'from-amber-500 via-yellow-600 to-orange-600',
      bgGradient: 'from-amber-50 to-orange-50'
    },
    {
      icon: Users,
      value: '85%',
      description: t('silentEpidemicNew.stat3'),
      gradient: 'from-slate-500 via-slate-600 to-gray-700',
      bgGradient: 'from-slate-50 to-gray-50'
    }
  ];

  // Auto-play for mobile
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % stats.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, stats.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % stats.length);
    setIsAutoPlaying(false);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + stats.length) % stats.length);
    setIsAutoPlaying(false);
  };

  return (
    <section className="relative py-16 lg:py-20 bg-gradient-to-br from-slate-50 via-white to-slate-100 overflow-hidden">
      {/* Premium Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="creative-grid absolute inset-0 opacity-5" />
        <div className="absolute top-1/4 -left-20 w-64 h-64 bg-gradient-to-r from-red-200/10 to-orange-200/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-20 w-80 h-80 bg-gradient-to-r from-orange-200/10 to-red-200/10 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10 lg:mb-12">
          <h2 className="text-2xl lg:text-3xl font-light text-slate-900 mb-3 lg:mb-4">
            {t('silentEpidemicNew.title')}
          </h2>
          <p className="text-base lg:text-lg text-slate-700 max-w-5xl mx-auto leading-relaxed font-light">
            {t('silentEpidemicNew.subtitle')}
          </p>
        </div>

        {/* Desktop: 3 Stats Grid */}
        <div className="hidden md:grid md:grid-cols-3 gap-5 lg:gap-6 mb-10 lg:mb-12">
          {stats.map((stat, index) => (
            <div key={index} className="group">
              <div className="premium-card relative bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-500 border border-slate-200/50 overflow-hidden h-full">
                {/* Subtle Gradient Overlay - Only on Hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${stat.bgGradient} opacity-30 group-hover:opacity-40 transition-opacity duration-500`} />
                
                {/* Icon Circle */}
                <div className="relative mb-4 flex justify-center">
                  <div className={`w-14 h-14 bg-gradient-to-br ${stat.gradient} rounded-full flex items-center justify-center shadow-md group-hover:shadow-lg group-hover:scale-105 transition-all duration-500`}>
                    <stat.icon className="w-7 h-7 text-white" />
                  </div>
                </div>
                
                {/* Statistic Value */}
                <div className={`relative text-4xl lg:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r ${stat.gradient} mb-3 text-center group-hover:scale-105 transition-transform duration-500`}>
                  {stat.value}
                </div>
                
                {/* Description */}
                <p className="relative text-sm text-slate-700 leading-relaxed text-center group-hover:text-slate-900 transition-colors duration-500">
                  {stat.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile: Carousel */}
        <div className="md:hidden relative mb-12">
          <div className="relative overflow-hidden rounded-3xl">
            <div 
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {stats.map((stat, index) => (
                <div key={index} className="w-full flex-shrink-0 px-4">
                  <div className={`premium-card relative bg-gradient-to-br ${stat.bgGradient} rounded-3xl p-8 shadow-xl border border-slate-200/50 overflow-hidden`}>
                    {/* Icon */}
                    <div className="relative mb-6 flex justify-center">
                      <div className={`w-20 h-20 bg-gradient-to-br ${stat.gradient} rounded-full flex items-center justify-center shadow-lg`}>
                        <stat.icon className="w-10 h-10 text-white" />
                      </div>
                    </div>
                    
                    {/* Value */}
                    <div className={`relative text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r ${stat.gradient} mb-4 text-center`}>
                      {stat.value}
                    </div>
                    
                    {/* Description */}
                    <p className="relative text-sm text-slate-700 leading-relaxed text-center">
                      {stat.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Carousel Controls */}
          <div className="flex items-center justify-center gap-4 mt-6">
            <button
              onClick={prevSlide}
              className="p-2 rounded-full bg-white shadow-lg border border-slate-200 hover:scale-110 transition-transform duration-300"
            >
              <ChevronLeft className="w-5 h-5 text-slate-700" />
            </button>
            
            {/* Dots */}
            <div className="flex gap-2">
              {stats.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setCurrentSlide(index);
                    setIsAutoPlaying(false);
                  }}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    index === currentSlide 
                      ? 'w-8 bg-gradient-to-r from-red-600 to-orange-500' 
                      : 'w-2 bg-slate-300'
                  }`}
                />
              ))}
            </div>
            
            <button
              onClick={nextSlide}
              className="p-2 rounded-full bg-white shadow-lg border border-slate-200 hover:scale-110 transition-transform duration-300"
            >
              <ChevronRight className="w-5 h-5 text-slate-700" />
            </button>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="premium-card glass-card bg-white/70 backdrop-blur-xl border border-slate-200/50 rounded-2xl p-6 lg:p-8 shadow-lg text-center max-w-4xl mx-auto hover:shadow-xl transition-all duration-500 relative overflow-hidden group">
          {/* Animated Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-orange-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          
          <h3 className="relative text-xl lg:text-2xl font-light text-slate-900 mb-3 lg:mb-4">
            {t('silentEpidemicNew.bottomTitle')}
          </h3>
          <p className="relative text-base lg:text-lg text-slate-700 leading-relaxed font-light">
            {t('silentEpidemicNew.bottomDescription')}
          </p>
          
          {/* Decorative Element */}
          <div className="relative mt-4 lg:mt-6 flex justify-center items-center space-x-2">
            <div className="w-10 h-0.5 bg-gradient-to-r from-transparent via-purple-500 to-transparent rounded-full" />
            <div className="w-1.5 h-1.5 bg-gradient-to-r from-purple-500 to-orange-500 rounded-full animate-pulse" />
            <div className="w-10 h-0.5 bg-gradient-to-r from-transparent via-orange-500 to-transparent rounded-full" />
          </div>
        </div>
      </div>
    </section>
  );
};
